import { WILD_GAME_ONLY_SPECIES } from '@farmshare/utils';

/**
 * Remove the species the wild-game feature INTRODUCED from a species list,
 * unless the processor is in the wild-game release.
 *
 * The choke point for species visibility. Filtering here rather than in each
 * picker means a NEW species picker cannot leak game by forgetting to filter —
 * the same class of mistake that left stale species maps in four places
 * before.
 *
 * Filters `WILD_GAME_ONLY_SPECIES`, NOT `GAME_SPECIES`. `venison` is a game
 * species but predates the feature, so hiding it would strip an existing
 * option from processors who already take farmed venison — and leave an
 * existing venison animal rendering with a value missing from its own
 * dropdown. Only hide what the feature added.
 *
 * Deliberately free of React and of the module registry so it stays a plain
 * function: testable on its own, and usable anywhere a species list is
 * assembled rather than only inside a component.
 *
 * @param species the full species list.
 * @param wildGameEnabled whether wild game is on for this processor.
 * @returns the species the user should be offered.
 */
export function filterGameSpecies<T extends string>(
  species: readonly T[],
  wildGameEnabled: boolean,
): T[] {
  if (wildGameEnabled) return [...species];
  const hidden = new Set<string>(WILD_GAME_ONLY_SPECIES);
  return species.filter((s) => !hidden.has(String(s).toLowerCase()));
}
